import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getTemplateDef, fillTemplate } from "@/lib/emailTemplates";

export interface EmailTemplateRow {
  id: string;
  template_key: string;
  brand_id: string | null;
  subject: string;
  body: string;
  is_active: boolean;
}

export function useEmailTemplateRows(enabled = true) {
  return useQuery({
    queryKey: ["email-templates"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("email_templates")
        .select("id, template_key, brand_id, subject, body, is_active");
      if (error) throw error;
      return (data || []) as EmailTemplateRow[];
    },
    enabled,
    staleTime: 60_000,
  });
}

/**
 * Resolves a template for a key/brand: brand override -> shared default -> code default.
 * Returns the wording already filled with the supplied variables.
 */
export function useResolvedEmailTemplate(
  key: string,
  brandId: string | null | undefined,
  vars: Record<string, string | undefined | null>,
  enabled = true,
) {
  const { data: rows, isLoading } = useEmailTemplateRows(enabled);
  const def = getTemplateDef(key);

  const brandRow = rows?.find((r) => r.template_key === key && r.brand_id === brandId && r.is_active);
  const sharedRow = rows?.find((r) => r.template_key === key && r.brand_id === null && r.is_active);
  const chosen = brandRow || sharedRow;

  const rawSubject = chosen?.subject?.trim() ? chosen.subject : def?.subject ?? "";
  const rawBody = chosen?.body?.trim() ? chosen.body : def?.body ?? "";

  return {
    isLoading,
    subject: fillTemplate(rawSubject, vars),
    body: fillTemplate(rawBody, vars),
    source: brandRow ? ("brand" as const) : sharedRow ? ("shared" as const) : ("default" as const),
  };
}
